// ═══ Studio Projects API Client ═══

import type { StudioProject } from './types'

const BASE = '' // Same origin, proxied by Vite/nginx

function getToken(): string | null {
  return localStorage.getItem('session_token') || (document.cookie.match(/session=([^;]+)/)?.[1] ?? null)
}

function authHeaders(): Record<string, string> {
  const token = getToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

export interface ProjectListItem {
  id: string
  title: string
  owner_id: string
  visibility: string
  created_at: string
  updated_at: string
  role: string
}

export interface ProjectListResponse {
  owned: ProjectListItem[]
  shared: ProjectListItem[]
  public: ProjectListItem[]
}

export interface ProjectDetail {
  id: string
  title: string
  owner_id: string
  visibility: string
  project_data: StudioProject
  shares: { user_id: string; role: string }[]
  role: string
}

export async function listProjects(): Promise<ProjectListResponse> {
  const res = await fetch(`${BASE}/api/tech/studio/projects`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`Failed to list projects: ${res.status}`)
  return res.json()
}

export async function loadProject(id: string): Promise<ProjectDetail> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${id}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`Failed to load project: ${res.status}`)
  return res.json()
}

export async function saveProject(id: string, project: StudioProject): Promise<void> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ project_data: project, title: project.title }),
  })
  if (!res.ok) throw new Error(`Failed to save project: ${res.status}`)
}

export async function createProject(title: string, projectData?: StudioProject): Promise<{ id: string }> {
  const res = await fetch(`${BASE}/api/tech/studio/projects`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ title, project_data: projectData || {}, visibility: 'private' }),
  })
  if (!res.ok) throw new Error(`Failed to create project: ${res.status}`)
  return res.json()
}

export async function updateProjectSettings(
  id: string,
  updates: { title?: string; visibility?: string }
): Promise<void> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(updates),
  })
  if (!res.ok) throw new Error(`Failed to update project: ${res.status}`)
}

export async function shareProject(
  id: string,
  userId: string,
  role: string = 'editor'
): Promise<void> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${id}/share`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ user_id: userId, role }),
  })
  if (!res.ok) throw new Error(`Failed to share project: ${res.status}`)
}

export async function deleteProject(id: string): Promise<void> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to delete project: ${res.status}`)
}

// ═══ AI Background Generation ═══

export type BackgroundStyle = 'cinematic' | 'abstract' | 'minimal' | 'neon'

export async function generateBackground(
  lyrics: string,
  style: BackgroundStyle,
  sectionName?: string,
  sectionType?: string,
): Promise<{ ok: boolean; url: string }> {
  const res = await fetch(`${BASE}/api/tech/studio/generate/background`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ lyrics, style, sectionName, sectionType }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(err.error || `Generation failed: ${res.status}`)
  }
  return res.json()
}

// ═══ Canvas Notes API ═══

export interface CanvasNote {
  id: string
  projectId: string
  timestampMs: number
  text: string
  author: string
  authorLabel: string
  position: { x: number; y: number }
  targetWord?: string
  targetBlockId?: string
  status: 'open' | 'resolved'
  createdAt: string
}

export async function listNotes(projectId: string): Promise<CanvasNote[]> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${projectId}/notes`, { headers: authHeaders() })
  if (!res.ok) return []
  return res.json()
}

export async function addNote(projectId: string, note: {
  timestampMs: number
  text: string
  author?: string
  authorLabel?: string
  position?: { x: number; y: number }
  targetWord?: string
  targetBlockId?: string
}): Promise<{ ok: boolean; id: string }> {
  const res = await fetch(`${BASE}/api/tech/studio/projects/${projectId}/notes`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(note),
  })
  if (!res.ok) throw new Error(`Failed to add note: ${res.status}`)
  return res.json()
}

export async function updateNote(noteId: string, updates: {
  status?: 'open' | 'resolved'
  text?: string
}): Promise<void> {
  const res = await fetch(`${BASE}/api/tech/studio/notes/${noteId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(updates),
  })
  if (!res.ok) throw new Error(`Failed to update note: ${res.status}`)
}

// ═══ Persona Actions API ═══

export interface PersonaAction {
  id: string
  note_id: string
  project_id: string
  target_persona: string
  action_text: string
  context_word: string | null
  context_block_id: string | null
  timestamp_ms: number
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  response_note_id: string | null
  created_at: string
  completed_at: string | null
}

export async function listActions(projectId: string): Promise<PersonaAction[]> {
  const res = await fetch(`${BASE}/api/tech/studio/actions?project_id=${projectId}`, { headers: authHeaders() })
  if (!res.ok) return []
  const data = await res.json()
  return data.actions || []
}

// ═══ Multi-Format Export API ═══

export interface FormatPreset {
  key: string
  width: number
  height: number
  label: string
}

export async function listFormats(): Promise<FormatPreset[]> {
  const res = await fetch(`${BASE}/api/tech/studio/render/formats`, { headers: authHeaders() })
  if (!res.ok) return []
  const data = await res.json()
  return data.formats || []
}

export interface RenderJob {
  id: string
  status: 'queued' | 'rendering' | 'completed' | 'failed'
  progress: number
  format: string
  formatLabel: string
  width: number
  height: number
  outputUrl: string | null
  error: string | null
  startedAt: string
  completedAt?: string
  batchId?: string
}

export async function startRender(projectData: any, format: string = 'landscape'): Promise<{ ok: boolean; jobId: string }> {
  const res = await fetch(`${BASE}/api/tech/studio/render`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ project_data: projectData, format }),
  })
  if (!res.ok) throw new Error(`Failed to start render: ${res.status}`)
  return res.json()
}

export async function startBatchRender(projectData: any, formats: string[]): Promise<{ ok: boolean; batchId: string; jobs: { jobId: string; format: string; label: string }[] }> {
  const res = await fetch(`${BASE}/api/tech/studio/render/batch`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ project_data: projectData, formats }),
  })
  if (!res.ok) throw new Error(`Failed to start batch render: ${res.status}`)
  return res.json()
}

export async function getRenderStatus(jobId: string): Promise<RenderJob> {
  const res = await fetch(`${BASE}/api/tech/studio/render/${jobId}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`Failed to get render status: ${res.status}`)
  return res.json()
}

export async function listRecentRenders(): Promise<RenderJob[]> {
  const res = await fetch(`${BASE}/api/tech/studio/render`, { headers: authHeaders() })
  if (!res.ok) return []
  const data = await res.json()
  return data.jobs || []
}
